import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, MapPin, Phone } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CONTACT, waLink } from "../lib/site";
import { TikTokIcon } from "./FloatingWhatsApp";

export const Footer = () => {
  const { t } = useLang();
  const book = waLink("Ciao Mamma Rosa! Vorrei prenotare un tavolo.");

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/menu", label: t("nav.menu") },
    { to: "/story", label: t("nav.story") },
    { to: "/gallery", label: t("nav.gallery") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <footer className="relative bg-navy-deep border-t border-white/10" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="font-display text-4xl tracking-tight">
            Mamma <span className="italic text-gold">Rosa</span>
          </div>
          <p className="mt-5 max-w-sm text-cream/60 font-body leading-relaxed">
            {t("footer.tagline")}
          </p>
          <a
            href={book}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-book-btn"
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-gold/60 px-6 py-3 text-gold font-body text-sm tracking-wide hover:bg-gold hover:text-navy-deep transition-colors"
          >
            {t("footer.book")}
          </a>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-xs tracking-[0.25em] uppercase text-cream/40 font-body">
            {t("footer.explore")}
          </h4>
          <ul className="mt-5 space-y-3">
            {links.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="font-body text-cream/75 hover:text-gold transition-colors"
                  data-testid={`footer-link-${l.label}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-xs tracking-[0.25em] uppercase text-cream/40 font-body">
            {t("footer.visit")}
          </h4>
          <ul className="mt-5 space-y-4 font-body text-cream/75">
            <li className="flex items-start gap-3">
              <MapPin size={18} className="text-gold mt-0.5 shrink-0" />
              <a href={CONTACT.mapLink} target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">
                {CONTACT.address}
              </a>
            </li>
            {CONTACT.phones.map((p) => (
              <li key={p.tel} className="flex items-center gap-3">
                <Phone size={18} className="text-gold shrink-0" />
                <a href={`tel:${p.tel}`} data-testid={`footer-phone-${p.tel}`} className="hover:text-gold transition-colors">
                  {p.display}
                </a>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex items-center gap-4">
            <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="w-11 h-11 grid place-items-center rounded-full border border-white/15 text-cream/80 hover:border-gold hover:text-gold transition-colors">
              <Instagram size={18} />
            </a>
            <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="w-11 h-11 grid place-items-center rounded-full border border-white/15 text-cream/80 hover:border-gold hover:text-gold transition-colors">
              <Facebook size={18} />
            </a>
            <a href={CONTACT.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="footer-tiktok" className="w-11 h-11 grid place-items-center rounded-full border border-white/15 text-cream/80 hover:border-gold hover:text-gold transition-colors">
              <TikTokIcon size={18} />
            </a>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-cream/40 font-body text-xs tracking-wide">
          <span>© {new Date().getFullYear()} Mamma Rosa Ipsos — {t("footer.rights")}</span>
          <span>Ristorante Pizzeria Napoletana · Ipsos, Corfù</span>
        </div>
      </div>
    </footer>
  );
};
